import { PureComponent, createRef } from "react";
import MutationObserver from "mutation-observer";

import { getDisplayName } from "./getDisplayName";
import getElementOuterHeight from "./getElementOuterHeight";
import getStylePropertyValue from "./getStylePropertyValue";
import { handleImagesLoading } from "./handleImagesLoading";

export default function withContentHeight(WrappedComponent) {
  class WithContentHeight extends PureComponent {
    static displayName = `WithContentHeight(${getDisplayName(WrappedComponent)})`;

    node = createRef(null);

    constructor() {
      super();

      this.setContentHeightContainer = this.setContentHeightContainer.bind(this);
      this.reprocessContentHeight = this.reprocessContentHeight.bind(this);
    }

    setContentHeightContainer(container, onContentHeightChanged) {
      this.container = container;
      this.handleContentHeightChange = onContentHeightChanged;
      if (!container) return;

      this.reprocessContentHeight();
      this.mutationObserver = new MutationObserver(this.reprocessContentHeight);
      this.mutationObserver.observe(this.container, {
        childList: true,
        subtree: true
      });

      this.container.parentNode.addEventListener("resize", this.reprocessContentHeight);
    }

    reprocessContentHeight() {
      const { container } = this;
      if (!container) return;

      setTimeout(() => {
        // Ensure that the async height processor isn't run once the component is
        // unmounted, as it's run after the call stack and may be triggered after the
        // component was unmounted without this guard clause.
        if (this.isUnmounted) return;

        // This ensures that images trigger a "resize" DOM event when loaded, so the
        // WithContentHeight HOC is able to reprocess the actual child content height
        // even if images loading takes some time and images are not cached.
        handleImagesLoading(container);

        const { children } = container;

        const contentHeight = Object.keys(children).reduce((total, index) => {
          return total + getElementOuterHeight(children[index]);
        }, 0);
        const containerPaddingHeight =
          getStylePropertyValue(container, "paddingTop") +
          getStylePropertyValue(container, "paddingBottom");

        this.handleContentHeightChange(contentHeight + containerPaddingHeight);
      }, 0);
    }

    componentWillUnmount() {
      // Flag the component as unmounted to avoid the async height processor to be run
      // if it was queued before the component was unmounted.
      this.isUnmounted = true;

      if (this.container) {
        this.container.parentNode.removeEventListener(
          "resize",
          this.reprocessContentHeight
        );
      }

      if (this.mutationObserver) {
        this.mutationObserver.disconnect();
      }
    }

    render() {
      return (
        <WrappedComponent
          {...this.props}
          setContentHeightContainer={this.setContentHeightContainer}
        />
      );
    }
  }

  return WithContentHeight;
}
